<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  throttleConfig: { type: Object, required: true },
});

const emit = defineEmits(['update']);

const PROFILES = {
  conservative: {
    burst_size: 12,
    burst_delay_min_ms: 15000,
    burst_delay_max_ms: 30000,
    cooldown_min_seconds: 300,
    cooldown_max_seconds: 600,
  },
  moderate: {
    burst_size: 30,
    burst_delay_min_ms: 8000,
    burst_delay_max_ms: 15000,
    cooldown_min_seconds: 180,
    cooldown_max_seconds: 300,
  },
  aggressive: {
    burst_size: 45,
    burst_delay_min_ms: 4000,
    burst_delay_max_ms: 8000,
    cooldown_min_seconds: 60,
    cooldown_max_seconds: 120,
  },
};

const { t } = useI18n();

const selectedProfile = computed(
  () => props.throttleConfig.profile || 'moderate'
);
const isCustom = computed(() => selectedProfile.value === 'custom');

const profileCards = computed(() => [
  {
    key: 'conservative',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.CONSERVATIVE'),
    description: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.CONSERVATIVE_DESC'),
    rate: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.CONSERVATIVE_RATE'),
    icon: 'i-lucide-shield-check',
  },
  {
    key: 'moderate',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.MODERATE'),
    description: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.MODERATE_DESC'),
    rate: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.MODERATE_RATE'),
    icon: 'i-lucide-gauge',
    recommended: true,
  },
  {
    key: 'aggressive',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.AGGRESSIVE'),
    description: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.AGGRESSIVE_DESC'),
    rate: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.AGGRESSIVE_RATE'),
    icon: 'i-lucide-zap',
  },
  {
    key: 'custom',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.CUSTOM'),
    description: t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.CUSTOM_DESC'),
    rate: '',
    icon: 'i-lucide-sliders-horizontal',
  },
]);

const effectiveConfig = computed(() => {
  if (isCustom.value) {
    return {
      burst_size: props.throttleConfig.burst_size || 30,
      burst_delay_min_ms: props.throttleConfig.burst_delay_min_ms || 8000,
      burst_delay_max_ms: props.throttleConfig.burst_delay_max_ms || 15000,
      cooldown_min_seconds: props.throttleConfig.cooldown_min_seconds || 180,
      cooldown_max_seconds: props.throttleConfig.cooldown_max_seconds || 300,
    };
  }
  return PROFILES[selectedProfile.value] || PROFILES.moderate;
});

const customMsgsPerMinute = computed(() => {
  const {
    burst_size: bs,
    burst_delay_min_ms: bdMin,
    burst_delay_max_ms: bdMax,
    cooldown_min_seconds: cdMin,
    cooldown_max_seconds: cdMax,
  } = effectiveConfig.value;
  const avgDelaySec = (bdMin + bdMax) / 2000;
  const burstDuration = bs * avgDelaySec;
  const avgCooldown = (cdMin + cdMax) / 2;
  const cycleTime = burstDuration + avgCooldown;
  return Math.round((bs / cycleTime) * 60);
});

const showHighSpeedWarning = computed(
  () => isCustom.value && customMsgsPerMinute.value > 60
);
const showVeryHighSpeedWarning = computed(
  () => isCustom.value && customMsgsPerMinute.value > 100
);

const selectProfile = key => {
  if (key === 'custom') {
    emit('update', {
      profile: 'custom',
      burst_size: 30,
      burst_delay_min_ms: 8000,
      burst_delay_max_ms: 15000,
      cooldown_min_seconds: 180,
      cooldown_max_seconds: 300,
    });
  } else {
    emit('update', { profile: key, ...PROFILES[key] });
  }
};

const updateCustomField = (field, value) => {
  const numValue = parseInt(value, 10) || 0;
  emit('update', { ...props.throttleConfig, [field]: numValue });
};

const updateDelayField = (field, valueInSeconds) => {
  const seconds = parseInt(valueInSeconds, 10) || 0;
  emit('update', { ...props.throttleConfig, [field]: seconds * 1000 });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Profile Cards -->
    <div class="grid grid-cols-2 gap-3">
      <button
        v-for="card in profileCards"
        :key="card.key"
        type="button"
        class="flex flex-col gap-2 p-4 rounded-lg border-2 transition-colors text-left"
        :class="
          selectedProfile === card.key
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak hover:border-n-slate-6'
        "
        @click="selectProfile(card.key)"
      >
        <div class="flex items-center gap-2">
          <span
            class="size-4 shrink-0"
            :class="[
              card.icon,
              selectedProfile === card.key ? 'text-n-brand' : 'text-n-slate-10',
            ]"
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{ card.label }}
          </span>
          <span
            v-if="card.recommended"
            class="text-[10px] px-1.5 py-0.5 rounded bg-n-brand/10 text-n-brand font-medium"
          >
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.RECOMMENDED') }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">{{ card.description }}</span>
        <span v-if="card.rate" class="text-xs font-medium text-n-slate-10">
          {{ card.rate }}
        </span>
      </button>
    </div>

    <!-- Custom Parameters -->
    <div
      v-if="isCustom"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-2"
    >
      <div class="grid grid-cols-2 gap-3">
        <Input
          :model-value="String(throttleConfig.burst_size || '')"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_SIZE')"
          type="number"
          :min="1"
          :max="100"
          :message="
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_SIZE_HINT')
          "
          @update:model-value="v => updateCustomField('burst_size', v)"
        />
        <Input
          :model-value="
            String(Math.round((throttleConfig.burst_delay_min_ms || 0) / 1000))
          "
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_DELAY_MIN')"
          type="number"
          :min="1"
          :max="30"
          :message="
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_DELAY_MIN_HINT')
          "
          @update:model-value="v => updateDelayField('burst_delay_min_ms', v)"
        />
        <Input
          :model-value="
            String(Math.round((throttleConfig.burst_delay_max_ms || 0) / 1000))
          "
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_DELAY_MAX')"
          type="number"
          :min="1"
          :max="60"
          :message="
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.BURST_DELAY_MAX_HINT')
          "
          @update:model-value="v => updateDelayField('burst_delay_max_ms', v)"
        />
        <Input
          :model-value="String(throttleConfig.cooldown_min_seconds || '')"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.COOLDOWN_MIN')"
          type="number"
          :min="5"
          :max="300"
          :message="
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.COOLDOWN_MIN_HINT')
          "
          @update:model-value="
            v => updateCustomField('cooldown_min_seconds', v)
          "
        />
        <Input
          :model-value="String(throttleConfig.cooldown_max_seconds || '')"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.COOLDOWN_MAX')"
          type="number"
          :min="10"
          :max="600"
          :message="
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.COOLDOWN_MAX_HINT')
          "
          @update:model-value="
            v => updateCustomField('cooldown_max_seconds', v)
          "
        />
      </div>

      <div class="flex items-center gap-2 text-xs text-n-slate-11">
        <span class="i-lucide-activity size-3.5" />
        <span>{{
          t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.RATE_DISPLAY', {
            rate: customMsgsPerMinute,
          })
        }}</span>
      </div>

      <!-- High speed warning -->
      <div
        v-if="showVeryHighSpeedWarning"
        class="flex items-center gap-2 p-3 rounded-lg bg-n-ruby-2 text-n-ruby-11"
      >
        <span class="i-lucide-alert-octagon size-4 shrink-0" />
        <span class="text-xs">
          {{
            t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.WARNING_VERY_HIGH_SPEED')
          }}
        </span>
      </div>
      <div
        v-else-if="showHighSpeedWarning"
        class="flex items-center gap-2 p-3 rounded-lg bg-n-amber-2 text-n-amber-11"
      >
        <span class="i-lucide-alert-triangle size-4 shrink-0" />
        <span class="text-xs">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.WARNING_HIGH_SPEED') }}
        </span>
      </div>
    </div>
  </div>
</template>
